// Global window manager to track and focus detached windows

import { getActiveDetachedWindow, getActiveDetachedWindows } from './DetachedWindowManager';

interface WindowInfo {
  window: Window;
  roomId: string;
  title: string;
  tileable: boolean;
}

const TILE_COLUMN_GAP_PX = 0;
const TILE_ROW_GAP_PX = 0;
const TILE_MIN_WIDTH_PX = 320;
const TILE_MIN_HEIGHT_PX = 240;

interface TileGeometry {
  screenLeft: number;
  screenTop: number;
  columns: number;
  tileWidth: number;
  tileHeight: number;
  resizeWidth: number;
  resizeHeight: number;
}

interface WindowChromeSize {
  width: number;
  height: number;
}

interface RegisterWindowOptions {
  tileable?: boolean;
}

class WindowFocusManager {
  private windows: Map<string, WindowInfo> = new Map();

  private isTileableWindowId(roomId: string): boolean {
    if (roomId === 'community-chat') return false;
    if (roomId.startsWith('tournament-lobby-')) return false;
    return true;
  }

  private createWindowInfo(
    roomId: string,
    window: Window,
    title: string,
    options?: RegisterWindowOptions
  ): WindowInfo {
    return {
      window,
      roomId,
      title,
      tileable: options?.tileable ?? this.isTileableWindowId(roomId),
    };
  }

  private pruneClosedWindows(): WindowInfo[] {
    const openWindows: WindowInfo[] = [];

    this.windows.forEach((info, roomId) => {
      if (!info.window || info.window.closed) {
        this.windows.delete(roomId);
        return;
      }
      if (typeof info.tileable !== 'boolean') {
        info.tileable = this.isTileableWindowId(roomId);
      }
      openWindows.push(info);
    });

    return openWindows;
  }

  private getOpenWindowsForTiling(): WindowInfo[] {
    const openWindows = this.pruneClosedWindows();
    const knownWindowRefs = new Set(openWindows.map((info) => info.window));

    getActiveDetachedWindows().forEach(({ id, window: detachedWindow }) => {
      if (this.windows.has(id) || knownWindowRefs.has(detachedWindow)) {
        return;
      }

      const info: WindowInfo = {
        window: detachedWindow,
        roomId: id,
        title: 'Detached Window',
        tileable: this.isTileableWindowId(id),
      };

      this.windows.set(id, info);
      knownWindowRefs.add(detachedWindow);
      openWindows.push(info);
    });

    return openWindows.filter((info) => info.tileable);
  }

  private getDetachedWindowName(roomId: string): string {
    // Keep names stable and ASCII-only so `window.open('', name)` is safe/predictable.
    return `hs-room-${roomId}`;
  }

  /**
   * Register a detached window with the manager
   */
  registerWindow(roomId: string, window: Window, title: string, options?: RegisterWindowOptions): void {
    try {
      // Ensure the window has a deterministic name so it can be re-targeted by name.
      window.name = this.getDetachedWindowName(roomId);
    } catch {
      // ignore
    }
    this.windows.set(roomId, this.createWindowInfo(roomId, window, title, options));
  }

  /**
   * Unregister a window when it's closed
   */
  unregisterWindow(roomId: string): void {
    this.windows.delete(roomId);
  }

  /**
   * Focus a window by room ID
   */
  focusWindow(roomId: string, sourceWindow?: Window | null): boolean {
    let windowInfo = this.windows.get(roomId);
    let targetWindow = windowInfo?.window;

    if (!targetWindow || targetWindow.closed) {
      // Attempt to recover a live window reference from DetachedWindowManager.
      const recovered = getActiveDetachedWindow(roomId);
      if (recovered && !recovered.closed) {
        targetWindow = recovered;
        windowInfo = {
          window: recovered,
          roomId,
          title: windowInfo?.title || 'Poker Room',
          tileable: windowInfo?.tileable ?? this.isTileableWindowId(roomId),
        };
        this.windows.set(roomId, windowInfo);
      } else {
        // Clean up closed/missing windows
        this.unregisterWindow(roomId);
        return false;
      }
    }

    try {
      // Check if we're in Electron environment and use the proper focus method.
      // Keep the browser retarget/focus path below as a second attempt; some windows
      // (for example tournament lobbies) are tracked in the web registry but not in
      // Electron's room-id registry.
      if (typeof (window as any).hs?.focusWindowByRoomId === 'function') {
        try {
          (window as any).hs.focusWindowByRoomId(roomId);
        } catch {
          // continue with the web focus path below
        }
      }

      const desiredName = this.getDetachedWindowName(roomId);
      try {
        if (targetWindow.name !== desiredName) {
          targetWindow.name = desiredName;
        }
      } catch {
        // ignore
      }

      // Browser focus is best-effort, but must be synchronous (user gesture) to have a chance.
      try {
        targetWindow.focus();
      } catch {
        // ignore; we still try the retarget path below
      }

      // Some browsers are stricter about which windows can focus others (opener/user-activation context).
      // To improve reliability we also try to re-target the named window via `open('', name)` from a
      // few plausible driver windows:
      // - the window where the click occurred (for detached lobby portals)
      // - the popup's opener (common for tournament lobbies)
      // - the main window
      const drivers: Window[] = [];
      if (sourceWindow && !sourceWindow.closed) drivers.push(sourceWindow);
      const openerWindow =
        (targetWindow as any).opener && !(targetWindow as any).opener.closed ? (targetWindow as any).opener : null;
      if (openerWindow) drivers.push(openerWindow);
      if (!drivers.includes(window)) drivers.push(window);

      for (const driver of drivers) {
        try {
          const retargeted = driver.open('', desiredName);
          if (!retargeted) continue;

          let isDetachedWindow = false;
          try {
            isDetachedWindow = retargeted === targetWindow
              ? true
              : retargeted.document?.body?.dataset?.detached === 'true';
          } catch {
            // ignore
          }

          if (!isDetachedWindow) {
            // Defensive: if the browser created a new blank window instead of retargeting,
            // close it immediately to avoid orphaned popups.
            try { retargeted.close(); } catch { /* ignore */ }
            continue;
          }

          // Use the retargeted handle (in case the original reference was stale).
          targetWindow = retargeted;
          this.windows.set(roomId, this.createWindowInfo(
            roomId,
            retargeted,
            windowInfo?.title || 'Poker Room',
            { tileable: windowInfo?.tileable }
          ));
          try { retargeted.focus(); } catch { /* ignore */ }
          break;
        } catch {
          // ignore
        }
      }

      return true;
    } catch (error) {
      console.warn('Could not focus window:', error);
      return false;
    }
  }

  transferWindow(fromRoomId: string, toRoomId: string): void {
    const info = this.windows.get(fromRoomId);
    if (!info) return;

    this.windows.delete(fromRoomId);
    const next: WindowInfo = {
      ...info,
      roomId: toRoomId,
      tileable: this.isTileableWindowId(toRoomId),
    };

    try {
      next.window.name = this.getDetachedWindowName(toRoomId);
    } catch {
      // ignore
    }
    this.windows.set(toRoomId, next);
  }

  private getWindowChromeSize(openWindows: WindowInfo[]): WindowChromeSize {
    let width = 0;
    let height = 0;

    openWindows.forEach((info) => {
      if (!info.window || info.window.closed) return;

      try {
        width = Math.max(width, Math.ceil((info.window.outerWidth || 0) - (info.window.innerWidth || 0)));
        height = Math.max(height, Math.ceil((info.window.outerHeight || 0) - (info.window.innerHeight || 0)));
      } catch {
        // Browser security/window-manager restrictions can block measurements.
      }
    });

    return {
      width: Math.max(0, width),
      height: Math.max(0, height),
    };
  }

  private getTileGeometry(windowCount: number, chromeSize: WindowChromeSize): TileGeometry {
    const screenInfo = window.screen as any;
    const screenLeft = typeof screenInfo.availLeft === 'number' ? screenInfo.availLeft : 0;
    const screenTop = typeof screenInfo.availTop === 'number' ? screenInfo.availTop : 0;
    const screenWidth = Math.max(640, window.screen.availWidth || window.innerWidth || 1280);
    const screenHeight = Math.max(480, window.screen.availHeight || window.innerHeight || 720);
    const columns = Math.ceil(Math.sqrt(windowCount));
    const rows = Math.ceil(windowCount / columns);
    const availableWidth = screenWidth - TILE_COLUMN_GAP_PX * (columns + 1);
    const availableHeight = screenHeight - TILE_ROW_GAP_PX * (rows + 1);
    const tileWidth = Math.floor(availableWidth / columns);
    const tileHeight = Math.floor(availableHeight / rows);
    const resizeWidth = Math.max(TILE_MIN_WIDTH_PX, tileWidth - chromeSize.width);
    const resizeHeight = Math.max(TILE_MIN_HEIGHT_PX, tileHeight - chromeSize.height);

    return {
      screenLeft,
      screenTop,
      columns,
      tileWidth,
      tileHeight,
      resizeWidth,
      resizeHeight,
    };
  }

  private applyTileGeometry(openWindows: WindowInfo[], geometry: TileGeometry): void {
    openWindows.forEach((info, index) => {
      const column = index % geometry.columns;
      const row = Math.floor(index / geometry.columns);
      const left = geometry.screenLeft + TILE_COLUMN_GAP_PX + column * (geometry.tileWidth + TILE_COLUMN_GAP_PX);
      const top = geometry.screenTop + TILE_ROW_GAP_PX + row * (geometry.tileHeight + TILE_ROW_GAP_PX);

      try {
        info.window.resizeTo(geometry.resizeWidth, geometry.resizeHeight);
      } catch {
        // Browser window managers can deny resizing; positioning is still worth trying.
      }

      try {
        info.window.moveTo(left, top);
      } catch {
        // Browser window managers can deny moving.
      }

      this.focusWindow(info.roomId);
    });
  }

  tileWindows(): number {
    const openWindows = this.getOpenWindowsForTiling();
    if (openWindows.length === 0) return 0;

    const geometry = this.getTileGeometry(openWindows.length, this.getWindowChromeSize(openWindows));
    this.applyTileGeometry(openWindows, geometry);

    try {
      this.focusWindow(openWindows[openWindows.length - 1].roomId);
    } catch {
      // ignore
    }

    return openWindows.length;
  }
}

function getWindowFocusManagerSingleton(): WindowFocusManager {
  const w = window as any;
  if (!w.__HS_WINDOW_FOCUS_MANAGER) {
    w.__HS_WINDOW_FOCUS_MANAGER = new WindowFocusManager();
  }
  return w.__HS_WINDOW_FOCUS_MANAGER as WindowFocusManager;
}

// Export singleton instance (stable across Vite HMR to avoid losing focus mappings in dev)
export const windowFocusManager =
  typeof window !== 'undefined' ? getWindowFocusManagerSingleton() : new WindowFocusManager();
